/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import Imgix from 'react-imgix';
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { wrap } from 'popmotion';
import useMeasure from 'react-use-measure';
import { data } from './mockDatoData';
import {
  slideshowWrapper,
  slideshowControlsWrapper,
  slideshowControlButton,
  slideshowControlButtonDisabled,
  slideshowSlide,
  arrowWrapper,
  newWrapper,
} from './style';
import { useTheme } from '@mui/material';

const FadeSlideShow: React.FC<{
  slides: any[];
  currSlide: number;
  runOnChange: (slide: number) => void;
  infinite?: boolean;
  gap?: number;
  slidesInView?: number;
  slideCss?: SerializedStyles;
  slideshowControlsWrapperCss?: SerializedStyles;
}> = ({
  slides,
  runOnChange,
  currSlide,
  infinite = true,
  gap = 50,
  slidesInView = 1,
  slideCss,
  slideshowControlsWrapperCss,
  ...props
}) => {
  if (!slides) return null;
  const [slide, setPage] = useState(0);
  const slideIndex = wrap(0, slides.length, slide);
  const {
    allComponents: [{ images }],
  } = data;
  const leftArrow = images[0].image;
  const rightArrow = images[1].image;
  const [allowScroll, setAllowScroll] = useState(false);
  const theme = useTheme();
  const [fadeSlideShowRef, bounds] = useMeasure();
  const fadeSlideShowRefWidth = bounds.width || theme?.breakpoints?.xl;
  const isMobile = fadeSlideShowRefWidth <= 900;
  const getSlideWidth = () => {
    const w = fadeSlideShowRefWidth / (slidesInView + 1);
    return `${isMobile ? fadeSlideShowRefWidth - 50 : w}px`;
  };

  const getMarginWidth = () => {
    const w = fadeSlideShowRefWidth / (slidesInView + 1) / 2;
    return `${w}px`;
  };

  useEffect(() => {
    if (allowScroll) {
      const handleTouch = event => {
        event.stopPropagation();
      };
      document.documentElement.addEventListener('touchmove', handleTouch);
      return () => {
        document.documentElement.removeEventListener('touchmove', handleTouch);
      };
    }
    return () => {};
  }, [allowScroll]);

  const paginate = (newDirection: number) => {
    if (!infinite) {
      if (slide + newDirection > slides.length - 1) {
        setPage(slides.length - 1);
        return;
      }
      if (slide + newDirection <= 0) {
        setPage(0);
        return;
      }
    }
    setPage(s => s + newDirection);
  };

  const swipeConfidenceThreshold = 100;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  useEffect(() => {
    if (runOnChange) {
      if (infinite) {
        runOnChange(1);
      } else {
        runOnChange(0);
      }
    }
  }, []);

  useEffect(() => {
    if (runOnChange) {
      runOnChange(Number.isNaN(slideIndex) ? 0 : slideIndex);
    }
  }, [slide]);

  useEffect(() => {
    const distance = currSlide - slideIndex;
    paginate(distance);
  }, [currSlide]);

  const isFirstSlide = !infinite && slideIndex === 0;
  const isLastSlide =
    (!infinite && slideIndex >= slides.length - 1) ||
    (!infinite && slideIndex >= slides?.length - slidesInView);
  const slideWidth = getSlideWidth();
  const marginWidth = getMarginWidth();

  return (
    <div
      css={css`
        ${slideshowWrapper}
      `}
      ref={fadeSlideShowRef}
      {...props}
    >
      <div
        css={css`
          ${slideshowControlsWrapper}
          ${slideshowControlsWrapperCss}
        `}
        aria-hidden="true"
      >
        <div>
          {isFirstSlide ? null : (
            <button
              css={css`
                ${slideshowControlButton}
                width: ${marginWidth};
                ${isFirstSlide ? slideshowControlButtonDisabled : ''}
              `}
              className="slide-control-left"
              onClick={() => (!isFirstSlide ? paginate(-1) : null)}
              tabIndex={-1}
            >
              <div css={arrowWrapper}>
                <Imgix
                  src={leftArrow.url}
                  htmlAttributes={{ alt: leftArrow.alt }}
                  sizes="36px"
                />
              </div>
            </button>
          )}
        </div>

        <div>
          {isLastSlide ? null : (
            <button
              css={css`
                ${slideshowControlButton}
                width: ${marginWidth};
                ${isLastSlide ? slideshowControlButtonDisabled : ''}
              `}
              onClick={() => (!isLastSlide ? paginate(1) : null)}
              className="slide-control-right"
              tabIndex={-1}
            >
              <div css={arrowWrapper}>
                <Imgix
                  src={rightArrow.url}
                  htmlAttributes={{ alt: rightArrow.alt }}
                  sizes="36px"
                />
              </div>
            </button>
          )}
        </div>
      </div>
      <motion.div
        drag="x"
        dragDirectionLock
        dragPropagation
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={1}
        onDragStart={(event, info) => {
          setAllowScroll(Math.abs(info.delta.y) > Math.abs(info.delta.x));
        }}
        onDragEnd={(e, { offset, velocity }) => {
          const swipe = swipePower(offset.x, velocity.x);

          if (swipe < -swipeConfidenceThreshold) {
            paginate(1);
          } else if (swipe > swipeConfidenceThreshold) {
            paginate(-1);
          }
        }}
        animate={{
          x: `calc(${
            isMobile
              ? `25px`
              : `-${marginWidth} - ${
                  infinite
                    ? `${gap * Math.max(1, slidesInView - 1)}px`
                    : `-${marginWidth} - -${marginWidth} - ${gap}px`
                }`
          } - ${
            isMobile
              ? `((${fadeSlideShowRefWidth}px * ${slideIndex}) - ${
                  25 * (slideIndex + 1)
                }px)`
              : `(${slideWidth} * ${slideIndex})`
          } - ${isMobile ? `25px` : `(${gap}px * ${slideIndex})`})`,
        }}
        transition={{
          // easings: [0.33, 1, 0.68, 1],
          type: 'spring',
          stiffness: 160,
          damping: 20,
          mass: 1,
        }}
        css={css`
          ${newWrapper}
          gap: ${isMobile ? 25 : gap}px !important;
        `}
      >
        {infinite ? (
          <div
            css={css`
              ${slideshowSlide}
              width: ${slideWidth};
              min-width: ${slideWidth};
              ${slideCss};
            `}
          >
            {slides[slides.length - 1]}
          </div>
        ) : null}
        {slides.map((s, sIndex) => (
          <div
            key={`slide-${sIndex}`}
            css={css`
              ${slideshowSlide}
              width: ${slideWidth};
              min-width: ${slideWidth};
              ${slideCss};
            `}
          >
            {s}
          </div>
        ))}
        {infinite ? (
          <div
            css={css`
              ${slideshowSlide}
              width: ${slideWidth};
              min-width: ${slideWidth};
              ${slideCss};
            `}
          >
            {slides[0]}
          </div>
        ) : null}
      </motion.div>
    </div>
  );
};
export default FadeSlideShow;
